import React, { useRef, useState, useEffect } from "react";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  TextField,
  IconButton,
  Button,
  List,
  ListItemText,
  ListItemButton,
  Popover,
  ListItemIcon,
  Chip,
  Badge,
  Divider,
  Box,
  Typography,
  InputBase,
  Tooltip,
  MenuItem,
  Select,
  Autocomplete,
  Checkbox,
  Card,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  FilterList,
  Search,
  MoreVert,
  ArrowDropDown,
  DeleteOutline,
  FileDownload,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { useFilter } from "pages/FilterContext";
import { useExcelDownload } from "./useExcelDownload";
import { themes } from "services/constants";

export const CustomFilter = ({
  title,
  filterHeaders = [],
  data = [],
  onFilteredDataChange,
  newButtonComponent,
  // onDateFilterChange, // Add this prop
  defaultDateFilter,
  screenAccess,
  filteredData = [],
  showSaveButton = true,
  showReprocessButton = false,
  setShowReprocessDialog,
}) => {
  const { setValue } = useForm({
    mode: "onBlur",
  });
  const {
    selectedFilters,
    filterValues,
    activeFilter,
    anchor,
    setSelectedFilters,
    setFilterValues,
    setAnchor,
    setActiveFilter,
    updateFilterValues,
    handleResetFilters,
    // callOnce,
    // setCallOnce,
  } = useFilter();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [filteredName, setFilteredName] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openFilter = Boolean(anchorEl);
  const openIndividualFilter = Boolean(anchor);
  const { downloadExcel } = useExcelDownload();

  const iconButtonStyle = {
    backgroundColor: "action.hover",
    borderRadius: "50%",
    border: "1px solid",
    borderColor: "divider",
    color: "text.primary",
    opacity: 1,
    transition: "none",
    "&.Mui-disabled": {
      backgroundColor: "action.hover",
      color: "text.primary",
      opacity: 1,
    },
    "&:hover": {
      backgroundColor: "action.hover",
    },
  };

  // newly updated for date range
  // useEffect(() => {
  //   if (selectedFilters.some((f) => f.key === "date") && callOnce) {
  //     const dateFilter = selectedFilters.find((f) => f.key === "date");
  //     if (dateFilter && filterValues.date) {
  //       onDateFilterChange && onDateFilterChange(filterValues);
  //       setCallOnce(false);
  //     }
  //   }
  // }, [
  //   selectedFilters,
  //   filterValues,
  //   onDateFilterChange,
  //   setCallOnce,
  //   callOnce,
  // ]);

  useEffect(() => {
    let filtered = data;
    if (selectedFilters.length > 0) {
      filtered = filtered.filter((row) =>
        selectedFilters.every((filter) => {
          if (filter.Viewmode) return true;
          const value = filterValues[filter.key];
          if (!value) return true;
          const rowValue = row[filter.key];
          const fieldKey = filter.key;
          switch (filter.type) {
            case "string":
              const searchValue = value?.toLowerCase() || "";

              if (fieldKey === "start_day") {
                const dateOfMonth =
                  row?.date_of_month?.toString().toLowerCase() || "";
                const startDay = row?.start_day?.toString().toLowerCase() || "";

                return (
                  dateOfMonth.includes(searchValue) ||
                  startDay.includes(searchValue)
                );
              }

              if (Array.isArray(rowValue)) {
                return rowValue.some((item) =>
                  item?.toString().toLowerCase().includes(searchValue),
                );
              }

              return rowValue?.toString().toLowerCase().includes(searchValue);

            case "number":
              if (!value) return true;

              if (typeof value !== "object") {
                const filterNum = parseFloat(value);
                const rowNum = parseFloat(rowValue);
                return !isNaN(filterNum) && !isNaN(rowNum)
                  ? rowNum === filterNum
                  : false;
              }

              const filterNumber =
                value.number !== undefined && value.number !== ""
                  ? parseFloat(value.number)
                  : null;
              const rowNumber = parseFloat(rowValue);

              if (isNaN(rowNumber)) return false;

              if (filterNumber === null || isNaN(filterNumber)) {
                if (value.operator === "between") {
                  const fromNum =
                    value.fromNumber !== undefined && value.fromNumber !== ""
                      ? parseFloat(value.fromNumber)
                      : null;
                  const toNum =
                    value.toNumber !== undefined && value.toNumber !== ""
                      ? parseFloat(value.toNumber)
                      : null;

                  let isValid = true;

                  if (fromNum !== null && !isNaN(fromNum)) {
                    isValid = isValid && rowNumber >= fromNum;
                  }
                  if (toNum !== null && !isNaN(toNum)) {
                    isValid = isValid && rowNumber <= toNum;
                  }

                  return isValid;
                }
                return true;
              }

              switch (value.operator) {
                case "equals":
                  return rowNumber === filterNumber;
                case "notEquals":
                  return rowNumber !== filterNumber;
                case "greaterEquals":
                  return rowNumber >= filterNumber;
                case "lessEquals":
                  return rowNumber <= filterNumber;
                case "between":
                  const fromNum =
                    value.fromNumber !== undefined && value.fromNumber !== ""
                      ? parseFloat(value.fromNumber)
                      : null;
                  const toNum =
                    value.toNumber !== undefined && value.toNumber !== ""
                      ? parseFloat(value.toNumber)
                      : null;

                  let isValid = true;

                  if (fromNum !== null && !isNaN(fromNum)) {
                    isValid = isValid && rowNumber >= fromNum;
                  }
                  if (toNum !== null && !isNaN(toNum)) {
                    isValid = isValid && rowNumber <= toNum;
                  }

                  return isValid;
                default:
                  return rowNumber === filterNumber;
              }
            case "boolean":
              if (value === "true") return rowValue === true;
              if (value === "false") return rowValue === false;
              return true;

            case "date":
              if (!value) return true;

              try {
                const parseDMY = (dateString) => {
                  if (!dateString) return null;
                  const parts = dateString.split("-");
                  if (parts.length === 3 && parts[0].length === 2) {
                    return new Date(parts[2], parts[1] - 1, parts[0]);
                  }
                  return new Date(dateString);
                };

                if (
                  (value.operator === "equals" ||
                    value.operator === "greater" ||
                    value.operator === "less") &&
                  value.date
                ) {
                  const filterDate = parseDMY(value.date);
                  if (!rowValue) return false;
                  const rowDate = parseDMY(rowValue);

                  if (isNaN(filterDate.getTime()) || isNaN(rowDate.getTime()))
                    return false;

                  switch (value.operator) {
                    case "equals":
                      return (
                        rowDate.toDateString() === filterDate.toDateString()
                      );
                    case "greater":
                      return rowDate >= filterDate;
                    case "less":
                      return rowDate <= filterDate;
                    default:
                      return false;
                  }
                }

                if (
                  value.operator === "between" &&
                  (value.fromDate || value.toDate)
                ) {
                  const fromDate = value.fromDate
                    ? parseDMY(value.fromDate)
                    : null;
                  const toDate = value.toDate ? parseDMY(value.toDate) : null;

                  const currentField = filterHeaders.find(
                    (f) => f.key === fieldKey,
                  );

                  if (!currentField?.dateRangeFields) {
                    return false;
                  }

                  const { fromField, toField } = currentField.dateRangeFields;
                  if (!row[fromField] || !row[toField]) return false;

                  const recordFrom = parseDMY(row[fromField]);
                  const recordTo = parseDMY(row[toField]);

                  if (
                    !recordFrom ||
                    !recordTo ||
                    isNaN(recordFrom.getTime()) ||
                    isNaN(recordTo.getTime())
                  )
                    return false;

                  let isValid = true;

                  if (fromDate) {
                    const startOfFromDate = new Date(fromDate);
                    startOfFromDate.setHours(0, 0, 0, 0);
                    isValid = isValid && recordFrom >= startOfFromDate;
                  }
                  if (toDate) {
                    const endOfToDate = new Date(toDate);
                    endOfToDate.setHours(23, 59, 59, 999);
                    isValid = isValid && recordTo <= endOfToDate;
                  }

                  return isValid;
                }
                return true;
              } catch (error) {
                showToast("error", "Date filtering error");
                return true;
              }
            case "autocomplete":
              if (
                !value ||
                value === "" ||
                (Array.isArray(value) && value.includes("All"))
              ) {
                return true;
              }

              if (Array.isArray(value)) {
                return value.some(
                  (selectedValue) =>
                    rowValue?.toString().toLowerCase() ===
                    selectedValue.toString().toLowerCase(),
                );
              }

              return (
                rowValue?.toString().toLowerCase() ===
                value.toString().toLowerCase()
              );

            case "time":
              if (!value) return true;

              try {
                const timeToMinutes = (timeStr) => {
                  if (!timeStr) return null;
                  const [hours, minutes] = timeStr.split(":").map(Number);
                  return (hours || 0) * 60 + (minutes || 0);
                };

                if (
                  (value.operator === "equals" ||
                    value.operator === "greater" ||
                    value.operator === "less") &&
                  value.time
                ) {
                  const filterTime = timeToMinutes(value.time);
                  const rowTime = timeToMinutes(rowValue);

                  if (filterTime === null || rowTime === null) return false;

                  switch (value.operator) {
                    case "equals":
                      return rowTime === filterTime;
                    case "greater":
                      return rowTime >= filterTime;
                    case "less":
                      return rowTime <= filterTime;
                    default:
                      return false;
                  }
                }

                if (
                  value.operator === "between" &&
                  (value.fromTime || value.toTime)
                ) {
                  const fromTime = value.fromTime
                    ? timeToMinutes(value.fromTime)
                    : null;
                  const toTime = value.toTime
                    ? timeToMinutes(value.toTime)
                    : null;

                  const recordStart = timeToMinutes(row.start_time);
                  const recordEnd = timeToMinutes(row.end_time);

                  if (!recordStart || !recordEnd) return false;

                  const isRecordOvernight = recordEnd < recordStart;
                  const isFilterOvernight = toTime < fromTime;

                  let isValid = true;

                  if (isFilterOvernight) {
                    if (isRecordOvernight) {
                      const recordEndAdj = recordEnd + 1440;
                      const toTimeAdj = toTime + 1440;

                      if (fromTime) {
                        const check =
                          recordStart >= fromTime && recordStart <= toTimeAdj;
                        isValid = isValid && check;
                      }

                      if (toTime) {
                        const check =
                          recordEndAdj >= fromTime && recordEndAdj <= toTimeAdj;
                        isValid = isValid && check;
                      }
                    } else {
                      const fitsInFirstPart =
                        recordStart >= fromTime && recordEnd <= 1440;
                      const fitsInSecondPart =
                        recordStart >= 0 && recordEnd <= toTime;

                      const check = fitsInFirstPart || fitsInSecondPart;
                      isValid = isValid && check;
                    }
                  } else {
                    if (isRecordOvernight) {
                      return false;
                    }

                    if (fromTime) {
                      const check = recordStart >= fromTime;
                      isValid = isValid && check;
                    }

                    if (toTime) {
                      const check = recordEnd <= toTime;
                      isValid = isValid && check;
                    }
                  }
                  return isValid;
                }

                return true;
              } catch (error) {
                showToast("error", error);
                return true;
              }
            default:
              return true;
          }
        }),
      );
    }

    const getNestedValue = (obj, keyPath) => {
      return keyPath.split(".").reduce((acc, key) => acc?.[key], obj);
    };

    if (query?.trim()) {
      const lowerQuery = query.toLowerCase();
      const searchKeys = filterHeaders.map((header) => header.key);

      filtered = filtered.filter((item) =>
        searchKeys.some((key) => {
          const value = getNestedValue(item, key);
          return value?.toString().toLowerCase().includes(lowerQuery);
        }),
      );
    }

    onFilteredDataChange(filtered);
  }, [
    selectedFilters,
    filterValues,
    data,
    query,
    onFilteredDataChange,
    filterHeaders,
  ]);

  //newly added

  useEffect(() => {
    if (defaultDateFilter) {
      const dateFilterExists = selectedFilters.some((f) => f.key === "date");
      if (!dateFilterExists) {
        const dateFilterHeader = filterHeaders.find((f) => f.key === "date");
        if (dateFilterHeader) {
          setSelectedFilters((prev) => [...prev, dateFilterHeader]);
          setFilterValues((prev) => ({
            ...prev,
            date: defaultDateFilter,
          }));
        }
      } else if (filterValues.date === undefined || filterValues.date === "") {
        // If date filter exists but has no value, set default
        setFilterValues((prev) => ({
          ...prev,
          date: defaultDateFilter,
        }));
      }
    }
  }, [
    defaultDateFilter,
    filterHeaders,
    selectedFilters,
    setFilterValues,
    setSelectedFilters,
    filterValues,
  ]);

  const handleClick = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleOpen = (event, filter) => {
    setAnchor(event.currentTarget);
    setActiveFilter(filter);
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleDeleteFilter = () => {
    if (activeFilter) {
      setSelectedFilters((prev) =>
        prev.filter((f) => f.key !== activeFilter.key),
      );
      setFilterValues((prev) => {
        const copy = { ...prev };
        delete copy[activeFilter.key];
        return copy;
      });
    }
    setMenuAnchor(null);
    setAnchor(null);
  };

  const getDynamicOptions = (filterKey) => {
    if (!data || data.length === 0) return [];

    const uniqueValues = [
      ...new Set(data.map((item) => item[filterKey])),
    ].filter(Boolean);
    return uniqueValues.sort();
  };

  const getCurrentValue = (value) => {
    if (
      !value ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return ["All"];
    }
    if (Array.isArray(value)) return value;
    return [value];
  };

  const handleExcelDownload = () => {
    downloadExcel(filteredData, filterHeaders, title, filterValues);
  };

  const handleMultipleSelection = (newValue, filterKey) => {
    const filter = selectedFilters.find((f) => f.key === filterKey);
    if (filter?.Viewmode) {
      const selected = newValue.length > 0 ? newValue[0] : "Group View";
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: [selected],
      }));
      return;
    }

    const lastSelected = newValue[newValue.length - 1];
    if (lastSelected === "All") {
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: ["All"],
      }));
    } else if (newValue.includes("All") && lastSelected !== "All") {
      const filteredValues = newValue.filter((opt) => opt !== "All");
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: filteredValues,
      }));
    } else {
      setFilterValues((prev) => ({
        ...prev,
        [filterKey]: newValue.length > 0 ? newValue : "",
      }));
    }
  };

  const renderFilterInput = (filter) => {
    const value = filterValues[filter.key] || "";

    if (filter.Viewmode) {
      const options = filter.options || ["Group View", "Employee View"];

      return (
        <Autocomplete
          multiple={false}
          fullWidth
          size="small"
          options={options}
          value={value}
          onChange={(event, newValue) => {
            setFilterValues((prev) => ({
              ...prev,
              [filter.key]: newValue ? [newValue] : ["Group View"],
            }));
          }}
          getOptionLabel={(option) => option}
          renderOption={(props, option) => (
            <li {...props}>
              <Typography>{option}</Typography>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select view"
            />
          )}
        />
      );
    }

    switch (filter.type) {
      case "string":
        return (
          <TextField
            fullWidth
            size="small"
            placeholder="Type a value"
            value={value}
            onChange={(e) =>
              setFilterValues((prev) => ({
                ...prev,
                [filter.key]: e.target.value,
              }))
            }
            variant="outlined"
          />
        );

      case "number":
        const numberValue = value || {
          operator: "equals",
          number: "",
          fromNumber: "",
          toNumber: "",
        };

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection:
                numberValue?.operator !== "between" ? "row" : "column",
              gap: 1,
              alignItems:
                numberValue?.operator !== "between" ? "center" : "stretch",
            }}
          >
            <Select
              size="small"
              value={numberValue?.operator || "equals"}
              onChange={(e) => {
                const newVal = {
                  operator: e.target.value,
                  number: numberValue?.number || "",
                  fromNumber: "",
                  toNumber: "",
                };
                setFilterValues((prev) => ({ ...prev, [filter.key]: newVal }));
              }}
              variant="outlined"
              sx={{
                minWidth: 90,
                width: numberValue?.operator === "between" ? "100%" : "auto",
                height: 40,
              }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="notEquals">≠</MenuItem>
              <MenuItem value="greaterEquals">≥</MenuItem>
              <MenuItem value="lessEquals">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>

            {numberValue?.operator !== "between" ? (
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Enter value"
                value={numberValue?.number || ""}
                onChange={(e) => {
                  const newVal = { ...numberValue, number: e.target.value };
                  setFilterValues((prev) => ({
                    ...prev,
                    [filter.key]: newVal,
                  }));
                }}
                variant="outlined"
              />
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  size="small"
                  type="number"
                  placeholder="From"
                  value={numberValue?.fromNumber || ""}
                  onChange={(e) => {
                    const newVal = {
                      ...numberValue,
                      fromNumber: e.target.value,
                    };
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: newVal,
                    }));
                  }}
                  variant="outlined"
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="To"
                  value={numberValue?.toNumber || ""}
                  onChange={(e) => {
                    const newVal = { ...numberValue, toNumber: e.target.value };
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: newVal,
                    }));
                  }}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        );

      case "date":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: value?.operator !== "between" ? "row" : "column",
              gap: 1,
            }}
          >
            <Select
              fullWidth
              size="small"
              value={value?.operator || "equals"}
              onChange={(e) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [filter.key]: {
                    operator: e.target.value,
                    date: value?.date || "",
                    fromDate: "",
                    toDate: "",
                  },
                }))
              }
              variant="outlined"
              sx={{ width: value?.operator === "between" ? "none" : 100 }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="greater">≥</MenuItem>
              <MenuItem value="less">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>

            {value?.operator !== "between" && (
              <TextField
                fullWidth
                size="small"
                type="date"
                value={
                  value?.date ? value.date.split("-").reverse().join("-") : ""
                }
                onChange={(e) =>
                  setFilterValues((prev) => ({
                    ...prev,
                    [filter.key]: {
                      ...value,
                      date: e.target.value.split("-").reverse().join("-"),
                    },
                  }))
                }
                variant="outlined"
              />
            )}

            {value?.operator === "between" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={
                    value?.fromDate
                      ? value.fromDate.split("-").reverse().join("-")
                      : ""
                  }
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: {
                        ...value,
                        fromDate: e.target.value.split("-").reverse().join("-"),
                      },
                    }))
                  }
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={
                    value?.toDate
                      ? value.toDate.split("-").reverse().join("-")
                      : ""
                  }
                  onChange={(e) =>
                    setFilterValues((prev) => ({
                      ...prev,
                      [filter.key]: {
                        ...value,
                        toDate: e.target.value.split("-").reverse().join("-"),
                      },
                    }))
                  }
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
        );

      case "time":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: value?.operator !== "between" ? "row" : "column",
              gap: 1,
            }}
          >
            <Select
              fullWidth
              size="small"
              value={value?.operator || "equals"}
              onChange={(e) =>
                setFilterValues((prev) => ({
                  ...prev,
                  [filter.key]: {
                    operator: e.target.value,
                    time: value?.time || "",
                    fromTime: value?.fromTime || "",
                    toTime: value?.toTime || "",
                  },
                }))
              }
              variant="outlined"
              sx={{
                width: value?.operator === "between" ? "none" : 80,
                height: 40,
                mt: 1,
              }}
            >
              <MenuItem value="equals">=</MenuItem>
              <MenuItem value="greater">≥</MenuItem>
              <MenuItem value="less">≤</MenuItem>
              {filter?.showBetweenOperator && (
                <MenuItem value="between">Between</MenuItem>
              )}
            </Select>

            {value?.operator !== "between" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["TimePicker"]}>
                  <div style={{ width: 150 }}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={value?.time ? dayjs(value.time, "HH:mm") : null}
                      onChange={(newValue) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            time: newValue
                              ? dayjs(newValue).format("HH:mm")
                              : "",
                          },
                        }));
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          variant: "outlined",
                        },
                      }}
                    />
                  </div>
                </DemoContainer>
              </LocalizationProvider>
            )}

            {value?.operator === "between" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["TimePicker"]}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={
                        value?.fromTime ? dayjs(value.fromTime, "HH:mm") : null
                      }
                      onChange={(newValue) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            fromTime: newValue
                              ? dayjs(newValue).format("HH:mm")
                              : "",
                          },
                        }));
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["TimePicker"]}>
                    <TimePicker
                      ampm={false}
                      viewRenderers={{
                        hours: renderTimeViewClock,
                        minutes: renderTimeViewClock,
                        seconds: renderTimeViewClock,
                      }}
                      value={
                        value?.toTime ? dayjs(value.toTime, "HH:mm") : null
                      }
                      onChange={(newValue) => {
                        setFilterValues((prev) => ({
                          ...prev,
                          [filter.key]: {
                            ...value,
                            toTime: newValue
                              ? dayjs(newValue).format("HH:mm")
                              : "",
                          },
                        }));
                      }}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          variant: "outlined",
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Box>
            )}
          </Box>
        );

      case "autocomplete":
        const dynamicOptions = filter.options || getDynamicOptions(filter.key);
        const options = ["All", ...dynamicOptions];

        return (
          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={options}
            disableCloseOnSelect
            value={getCurrentValue(value)}
            onChange={(event, newValue) => {
              handleMultipleSelection(newValue, filter.key);
            }}
            getOptionLabel={(option) => option}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  checked={selected}
                  sx={{
                    color: option === "primary.main",
                    "&.Mui-checked": {
                      color: option === "primary.main",
                    },
                  }}
                />
                <Typography
                  sx={{
                    color: option === "primary.main",
                  }}
                >
                  {option}
                </Typography>
              </li>
            )}
            filterOptions={(options, { inputValue }) =>
              options.filter((option) =>
                option
                  .toString()
                  .toLowerCase()
                  .includes(inputValue.toLowerCase()),
              )
            }
            ListboxProps={{
              style: {
                maxHeight: 200,
              },
            }}
            renderInput={(params) => (
              <TextField {...params} variant="outlined" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} />
              ))
            }
          />
        );
      case "boolean":
        return (
          <Select
            fullWidth
            size="small"
            value={value || ""}
            onChange={(e) =>
              setFilterValues((prev) => ({
                ...prev,
                [filter.key]: e.target.value,
              }))
            }
            variant="outlined"
            displayEmpty
          >
            <MenuItem value="">{filter.labels?.all || "All"}</MenuItem>
            <MenuItem value="true">{filter.labels?.true || "Yes"}</MenuItem>
            <MenuItem value="false">{filter.labels?.false || "No"}</MenuItem>
          </Select>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    return dateString;
  };

  const getChipDisplayValue = (value, filter) => {
    if (filter?.Viewmode) {
      if (Array.isArray(value) && value.length > 0) {
        return `: ${value[0]}`;
      }
      return `: ${value ?? ""}`;
    }
    if (Array.isArray(value) && filter?.type === "autocomplete") {
      if (value.length === 0 || value.includes("All")) {
        return filter.labels?.all || "All";
      }
      if (value.length === 1) {
        return `: ${value[0]}`;
      }
      if (value.length === 2) {
        return `: ${value.join(", ")}`;
      }
      return `: ${value.length} selected`;
    }
    if (filter.type === "boolean") {
      if (!value || value === "") {
        return filter.labels?.all || "All";
      }
      return value === "true"
        ? filter.labels?.true || "Yes"
        : filter.labels?.false || "No";
    }

    if (filter.type === "date" && typeof value === "object") {
      const operatorSymbol =
        {
          equals: "=",
          greater: "≥",
          less: "≤",
          between: "Between",
        }[value.operator] || "=";

      if (value.operator !== "between" && value.date) {
        return `: ${operatorSymbol} ${formatDate(value.date)}`;
      }

      if (value.operator === "between") {
        if (value.fromDate && value.toDate) {
          return `: ${operatorSymbol} ${formatDate(
            value.fromDate,
          )} - ${formatDate(value.toDate)}`;
        }
        if (value.fromDate) {
          return `: ${operatorSymbol} From ${formatDate(value.fromDate)}`;
        }
        if (value.toDate) {
          return `: ${operatorSymbol} To ${formatDate(value.toDate)}`;
        }
      }

      return filter.labels?.all || "All";
    }
    if (filter.type === "time" && typeof value === "object") {
      const operatorSymbol =
        {
          equals: "=",
          greater: "≥",
          less: "≤",
          between: "Between",
        }[value.operator] || "=";

      if (value.operator !== "between" && value.time) {
        return `: ${operatorSymbol} ${value.time}`;
      }

      if (value.operator === "between") {
        if (value.fromTime && value.toTime) {
          return `: ${operatorSymbol} ${value.fromTime} - ${value.toTime}`;
        }
        if (value.fromTime) {
          return `: ${operatorSymbol} From ${value.fromTime}`;
        }
        if (value.toTime) {
          return `: ${operatorSymbol} To ${value.toTime}`;
        }
      }

      return "";
    }
    if (filter.type === "number") {
      if (typeof value === "object" && value !== null) {
        const operatorSymbol =
          {
            equals: "=",
            notEquals: "≠",
            greaterEquals: "≥",
            lessEquals: "≤",
            between: "Between",
          }[value.operator] || "=";

        if (
          value.operator !== "between" &&
          value.number !== undefined &&
          value.number !== ""
        ) {
          return `: ${operatorSymbol} ${value.number}`;
        }

        if (value.operator === "between") {
          if (
            value.fromNumber !== undefined &&
            value.fromNumber !== "" &&
            value.toNumber !== undefined &&
            value.toNumber !== ""
          ) {
            return `: ${operatorSymbol} ${value.fromNumber} - ${value.toNumber}`;
          }
          if (value.fromNumber !== undefined && value.fromNumber !== "") {
            return `: ${operatorSymbol} From ${value.fromNumber}`;
          }
          if (value.toNumber !== undefined && value.toNumber !== "") {
            return `: ${operatorSymbol} To ${value.toNumber}`;
          }
        }

        return "";
      } else {
        return value !== undefined && value !== "" && value !== null
          ? `: ${value}`
          : "";
      }
    }

    if (
      value === undefined ||
      value === null ||
      value === "" ||
      (typeof value === "object" &&
        !value?.date &&
        !value?.fromDate &&
        !value?.toDate)
    ) {
      if (filter.type === "autocomplete" || filter.type === "boolean") {
        return filter.labels?.all || "All";
      }
      return "";
    }

    return `: ${value}`;
  };

  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  return (
    <>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title} : {filteredData?.length ?? 0}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: open ? 2 : 50,
              overflow: "hidden",
              bgcolor: !open && themes?.primary,
              p: 0,
              width: "fit-content",
              transition: "border-radius 0.3s ease",
            }}
          >
            <Tooltip title="Search" arrow slotProps={tooltipStyleProps}>
              <IconButton
                onClick={() => setOpen((prev) => !prev)}
                sx={{
                  borderRadius: open ? 0 : 50,
                  borderRight: open ? "1px solid" : "none",
                  borderColor: "divider",
                  transition: "border-radius 0.3s ease",
                }}
              >
                <Search />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                height: 40,
                width: open ? 200 : 0,
                transition: "width 0.3s ease",
                display: "flex",
                alignItems: "center",
              }}
            >
              <InputBase
                inputRef={inputRef}
                placeholder="Type to search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                  flex: 1,
                  opacity: open ? 1 : 0,
                  transition: "opacity 0.2s ease",
                  px: 2,
                }}
              />
            </Box>
          </Card>
          {showReprocessButton ? (
            <Tooltip title="Reprocess" arrow slotProps={tooltipStyleProps}>
              <IconButton
                onClick={() => {
                  setShowReprocessDialog(true);
                }}
                sx={{
                  ...iconButtonStyle,
                  color: themes?.blackColor,
                }}
                disabled={filteredData?.length === 0 || data?.length === 0}
              >
                <ReplayIcon />
              </IconButton>
            </Tooltip>
          ) : null}

          <Tooltip title="Report" arrow slotProps={tooltipStyleProps}>
            <IconButton
              onClick={() => {
                if (screenAccess) {
                  handleExcelDownload();
                } else {
                  showToast(
                    "error",
                    "Access denied: You are not authorized to download report",
                  );
                }
              }}
              sx={{
                ...iconButtonStyle,
                color: themes?.blackColor,
              }}
              disabled={filteredData?.length === 0 || data?.length === 0}
            >
              <FileDownload />
            </IconButton>
          </Tooltip>

          <Badge
            color="warning"
            overlap="circular"
            badgeContent={selectedFilters?.length || 0}
            invisible={!selectedFilters?.length}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.7rem",
                fontWeight: 600,
                minWidth: 18,
                height: 18,
              },
            }}
          >
            <Tooltip title="Filter" arrow slotProps={tooltipStyleProps}>
              <span>
                <IconButton
                  onClick={(event) => {
                    if (screenAccess) {
                      handleClick(event);
                    } else {
                      showToast(
                        "error",
                        "Access denied: You are not authorized to Add filter",
                      );
                    }
                  }}
                  disabled={selectedFilters?.length >= 1}
                  sx={iconButtonStyle}
                >
                  <FilterList />
                </IconButton>
              </span>
            </Tooltip>
          </Badge>

          {/* Filter Popover */}
          <Popover
            open={openFilter}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                width: 220,
                borderRadius: 2,
              },
            }}
          >
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Filter by..."
                value={filteredName}
                onChange={(e) => setFilteredName(e.target.value)}
                variant="outlined"
              />
            </Box>

            <List
              dense
              sx={{
                pt: 0,
                pb: 0.7,
                overflowY: "auto",
                height: filterHeaders?.length > 7 ? 200 : "auto",
              }}
            >
              {filterHeaders
                .filter(
                  (f) =>
                    f?.key &&
                    f?.name &&
                    f?.name
                      ?.toLowerCase()
                      ?.includes(filteredName?.toLowerCase() || "") &&
                    !selectedFilters.some((filter) => filter.key === f.key),
                )
                .map((f, i) => (
                  <ListItemButton
                    key={i}
                    onClick={(e) => {
                      setSelectedFilters((prev) => [...prev, f]);
                      setAnchorEl(null);
                      setValue("filter_clicked", true);
                      if (f.type === "date") {
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: {
                            operator: "equals",
                            date: "",
                            fromDate: "",
                            toDate: "",
                          },
                        }));
                      }
                      if (f.type === "time") {
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: {
                            operator: "equals",
                            time: "",
                            fromTime: "",
                            toTime: "",
                          },
                        }));
                      }

                      if (f.type === "autocomplete") {
                        setFilterValues((prev) => ({
                          ...prev,
                          [f.key]: ["All"],
                        }));
                      }

                      setTimeout(() => {
                        // Find the chip that matches the newly added filter
                        const allChips =
                          document.querySelectorAll(".MuiChip-root");
                        let targetChip = null;

                        // Look for a chip containing the filter name text
                        for (let chip of allChips) {
                          if (
                            chip.textContent &&
                            chip.textContent.includes(f.name)
                          ) {
                            targetChip = chip;
                            break;
                          }
                        }

                        if (targetChip) {
                          setAnchor(targetChip);
                          setActiveFilter(f);
                        }
                      }, 100);
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>{f.icon}</ListItemIcon>
                    <ListItemText
                      primary={f.name || "Unnamed"}
                      primaryTypographyProps={{ fontSize: 14 }}
                    />
                  </ListItemButton>
                ))}
            </List>
            {filterHeaders.filter(
              (f) =>
                f?.name &&
                f?.key &&
                f?.name
                  ?.toLowerCase()
                  ?.includes(filteredName?.toLowerCase() || "") &&
                !selectedFilters.some((filter) => filter.key === f.key),
            ).length === 0 && (
              <Typography
                variant="body2"
                sx={{ textAlign: "center", color: "#777" }}
              >
                No results
              </Typography>
            )}
          </Popover>

          {newButtonComponent}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ p: 1, ml: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 1.2, flexWrap: "wrap", mt: 1 }}>
            {selectedFilters.map((filter) => (
              <Badge
                color="warning"
                variant="dot"
                overlap="circular"
                sx={{
                  "& .MuiBadge-badge": {
                    top: 4,
                    right: 6,
                  },
                }}
              >
                <Chip
                  key={filter.key}
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {filter.name}

                      <Box component="span" sx={{ color: "warning.main" }}>
                        {getChipDisplayValue(filterValues[filter.key], filter)}
                      </Box>

                      <ArrowDropDown fontSize="small" />
                    </Box>
                  }
                  onClick={(e) => handleOpen(e, filter)}
                  sx={{
                    borderRadius: "6px",
                    backgroundColor: "#e0e0e0",
                    color: "#000",
                  }}
                />
              </Badge>
            ))}
            {selectedFilters?.length > 0 && (
              <Chip
                label="+ filter"
                sx={{
                  color: "#000",
                  borderRadius: "6px",
                  height: 30,
                  backgroundColor: "#e0e0e0",
                }}
                clickable
                onClick={(e) => {
                  if (screenAccess) {
                    setAnchorEl(e.currentTarget);
                  } else {
                    showToast(
                      "error",
                      "Access denied: You are not authorized to add filter",
                    );
                  }
                }}
              />
            )}

            <Popover
              open={openIndividualFilter}
              anchorEl={anchor}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  p: 1.5,
                  width: 260,
                  borderRadius: 2,
                  mt: 1,
                },
              }}
            >
              {activeFilter && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">{activeFilter.name}</Typography>
                    <IconButton size="small">
                      <MoreVert fontSize="small" onClick={handleMenuOpen} />
                    </IconButton>
                  </Box>
                  {renderFilterInput(activeFilter)}
                </>
              )}
            </Popover>

            {/* Delete Filter Menu */}
            <Popover
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  borderRadius: 1,
                  minWidth: 140,
                },
              }}
            >
              <Button
                fullWidth
                variant="text"
                startIcon={<DeleteOutline />}
                onClick={handleDeleteFilter}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  color: "black",
                  fontWeight: "400",
                  "&:hover": {
                    color: "red",
                    backgroundColor: "transparent",
                  },
                }}
              >
                Delete Filter
              </Button>
            </Popover>
          </Box>
          {selectedFilters?.length > 0 && showSaveButton && (
            <Box sx={{ display: "flex", gap: 1, mr: 0.8 }}>
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  if (!screenAccess) {
                    showToast(
                      "error",
                      "Access denied: You are not authorized to reset filter",
                    );
                    return;
                  }

                  if (selectedFilters?.length > 0) {
                    handleResetFilters();
                  }
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                onClick={() => {
                  if (!screenAccess) {
                    showToast(
                      "error",
                      "Access denied: You are not authorized to reset filter",
                    );
                    return;
                  }
                  if (selectedFilters?.length > 0) {
                    updateFilterValues();
                  }
                }}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};
